import * as React from 'react';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { PrimaryButton } from '@fluentui/react/lib/Button';
import { useLocalization } from '@microsoft/portal-app/lib/shared/localization';
import { useMount } from '@uifabric/portal-ux/lib/hooks/useMount';
import { ContactPreferenceCheckbox } from './ContactPreferenceCheckbox';
import { ContactPreferenceEmailInput } from './ContactPreferenceEmailInput';
import { ContactPreferenceFooter } from './ContactPreferenceFooter';
import { ContactPreferencePhoneInput } from './ContactPreferencePhoneInput';
import { ContactPreferenceContent } from './ContactPreferenceContent';
import { selectContactPreferenceAdmin } from '../selectors';
import { effectContactPreferenceAdminSave, effectContactPreferenceAdminFetch } from '../effects';
import { useSelector } from '../../redux';
import { useBinding } from '../../shared/useBinding';

export const ContactPreferenceAdmin: React.FC = () => {
  const [t, isLocalizationReady] = useLocalization(['Privacy', 'Account', 'App', 'CountryCodes']);
  const dispatch = useDispatch();
  const state = useSelector(selectContactPreferenceAdmin);
  const ready = useMount(() => state.shouldFetch && dispatch(effectContactPreferenceAdminFetch()));

  // Form State
  const [offersOptIn, onOffersOptInChange] = useBinding(state.offersOptIn, (newValue: boolean) => newValue);
  const [partnersOptIn, onPartnersOptInChange] = useBinding(state.partnersOptIn, (newValue: boolean) => newValue);
  const [partnersCountryCode, onPartnersCountryCodeChange] = useBinding(
    state.partnersCountryCode,
    (newValue: string) => newValue
  );
  const [partnersPhoneNumber, onPartnersPhoneNumberChange] = useBinding(
    state.partnersPhoneNumber,
    (newValue: string) => newValue
  );
  const [partnersEmailChecked, onPartnersEmailCheckedChange] = useBinding(
    !!state.partnersEmail,
    (newValue: boolean) => newValue
  );
  const [partnersEmail, onPartnersEmailChange] = useBinding(state.partnersEmail, (newValue: string) => newValue);

  const onSave = useCallback(() => {
    dispatch(
      effectContactPreferenceAdminSave({
        offersOptIn: offersOptIn ?? false,
        partnersOptIn: partnersOptIn ?? false,
        partnersEmail: partnersEmail ?? '',
        partnersCountryCode: partnersCountryCode ?? '1',
        partnersPhoneNumber: partnersPhoneNumber ?? ''
      })
    );
  }, [offersOptIn, partnersOptIn, partnersEmail, partnersCountryCode, partnersPhoneNumber]);

  // Don't show errors if the input has changed.
  const partnersPhoneNumberError =
    state.partnersCountryCode === partnersCountryCode && state.partnersPhoneNumber === partnersPhoneNumber
      ? state.partnersPhoneNumberError ?? undefined
      : undefined;
  const partnersEmailError = state.partnersEmail === partnersEmail ? state.partnersEmailError ?? undefined : undefined;

  if (!isLocalizationReady) {
    return null;
  }

  const disabled = !ready || state.readonly;

  return (
    <ContactPreferenceContent>
      <h4>{t('contactPreferenceAdminHeader')}</h4>
      <ContactPreferenceCheckbox
        label={t('contactPreferenceOffersLabel')}
        disabled={disabled}
        checked={offersOptIn}
        onChange={onOffersOptInChange}
      />
      <ContactPreferenceCheckbox
        label={t('contactPreferencePartnersLabel')}
        disabled={disabled}
        checked={partnersOptIn}
        onChange={onPartnersOptInChange}
      />
      <p>{t('contactPreferencePhoneMessage')}</p>
      <ContactPreferencePhoneInput
        disabled={disabled}
        error={partnersPhoneNumberError}
        countryCode={partnersCountryCode}
        subscriberNumber={partnersPhoneNumber}
        onCountryCodeChange={onPartnersCountryCodeChange}
        onSubscriberNumberChange={onPartnersPhoneNumberChange}
      />
      <h4>{t('contactPreferenceEmailHeader')}</h4>
      <ContactPreferenceCheckbox
        label={t('contactPreferenceEmailLabel')}
        disabled={disabled}
        checked={partnersEmailChecked}
        onChange={onPartnersEmailCheckedChange}
      />
      {(partnersEmailChecked || partnersEmailError === 'emailAddressRequired') && (
        <ContactPreferenceEmailInput
          disabled={disabled || !partnersEmailChecked}
          error={partnersEmailError}
          value={partnersEmail}
          onChange={onPartnersEmailChange}
        />
      )}
      <ContactPreferenceFooter activity={state.activity}>
        <PrimaryButton text={t('save')} disabled={disabled} onClick={onSave} />
      </ContactPreferenceFooter>
    </ContactPreferenceContent>
  );
};
